import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Heart, Award, Users, Target, ArrowRight, GraduationCap, Star } from 'lucide-react';
import { playSoundIfEnabled } from '../utils/sounds';
import { aboutInfo } from '../mock/mockData';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { faqs } from '../mock/mockData';

const About = () => {
  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      {/* Hero Section */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-light-pink/10 rounded-full px-4 py-2 mb-4">
              <Sparkles className="w-4 h-4 text-light-pink" />
              <span className="text-light-pink text-sm font-mono uppercase tracking-wider">
                About Us
              </span>
            </div>
            <h1 className="text-5xl lg:text-6xl font-display font-bold text-white mb-6">
              {aboutInfo.studioName}
            </h1>
            <p className="text-2xl text-light-pink mb-4 font-semibold">
              {aboutInfo.tagline}
            </p>
            <p className="text-xl text-mid-grey max-w-4xl mx-auto leading-relaxed">
              {aboutInfo.mission}
            </p>
          </div>

          {/* Image Grid - Real Photos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
            <div className="relative group overflow-hidden rounded-3xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_drama-kids-workshops/artifacts/75q2pyhu_IMG_6274.jpeg"
                alt="Young actor at INSPIRE STUDIO"
                className="w-full h-96 object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-6 left-6 text-white">
                <h3 className="text-2xl font-bold mb-2">Creative Expression</h3>
                <p className="text-light-pink">Where imagination comes to life</p>
              </div>
            </div>
            <div className="relative group overflow-hidden rounded-3xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_drama-kids-workshops/artifacts/0di3eedu_2A0A1B6C-CAB3-436F-84AE-3617611975FE.jpeg"
                alt="Drama workshop activities"
                className="w-full h-96 object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-6 left-6 text-white">
                <h3 className="text-2xl font-bold mb-2">Building Confidence</h3>
                <p className="text-mid-purple">Through the magic of theatre</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Section - Richard Kidd */}
      <section className="py-16 lg:py-24 bg-gradient-to-b from-black to-black relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 right-0 w-96 h-96 bg-light-pink/10 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Richard's Photo */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-light-pink/30 to-mid-purple/30 rounded-3xl blur-2xl" />
                <img 
                  src="https://customer-assets.emergentagent.com/job_drama-kids-workshops/artifacts/xyrjzcwi_IMG_7923.jpeg"
                  alt="Richard Kidd - Founder of INSPIRE STUDIO OXFORD"
                  className="relative rounded-3xl w-full shadow-2xl border-4 border-light-pink/20"
                />
              </div>

              {/* Richard's Bio */}
              <div>
                <div className="inline-flex items-center gap-2 bg-mid-purple/10 rounded-full px-4 py-2 mb-4">
                  <Star className="w-4 h-4 text-mid-purple" />
                  <span className="text-mid-purple text-sm font-mono uppercase tracking-wider">
                    Our Founder
                  </span>
                </div>
                <h2 className="text-4xl lg:text-5xl font-display font-bold text-white mb-4">
                  Richard Kidd
                </h2>
                <p className="text-xl text-light-pink mb-6 font-semibold">
                  Professional Actor & Drama Educator
                </p>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-4 bg-white/5 rounded-2xl p-6 border border-white/10">
                    <div className="w-12 h-12 bg-light-pink rounded-xl flex items-center justify-center flex-shrink-0">
                      <GraduationCap className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-2">Guildhall School of Music & Drama</h3>
                      <p className="text-mid-grey text-sm">
                        Trained at one of the UK's most prestigious drama conservatoires, 
                        learning from world-class professionals.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 bg-white/5 rounded-2xl p-6 border border-white/10">
                    <div className="w-12 h-12 bg-mid-purple rounded-xl flex items-center justify-center flex-shrink-0">
                      <Star className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-2">11 Years Professional Acting Career</h3>
                      <p className="text-mid-grey text-sm">
                        Extensive experience performing in theatre, bringing real-world 
                        industry knowledge to every workshop.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 bg-white/5 rounded-2xl p-6 border border-white/10">
                    <div className="w-12 h-12 bg-light-yellow rounded-xl flex items-center justify-center flex-shrink-0">
                      <Heart className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-2">Passion for Teaching</h3>
                      <p className="text-mid-grey text-sm">
                        Discovered a love for nurturing young talent and helping children 
                        discover the transformative power of drama and confidence.
                      </p>
                    </div>
                  </div>
                </div>

                <blockquote className="border-l-4 border-light-pink pl-6 py-4 text-white italic text-lg">
                  "After 11 years performing professionally, I found my true calling in teaching. 
                  Watching children discover their voice, build confidence, and fall in love with 
                  Shakespeare is the most rewarding work I've ever done."
                  <footer className="text-light-pink mt-2 not-italic text-sm">— Richard Kidd</footer>
                </blockquote>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-16 lg:py-24 bg-gradient-to-b from-black to-black relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-0 w-96 h-96 bg-light-pink/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-mid-purple/10 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-mid-purple/10 rounded-full px-4 py-2 mb-4">
              <Target className="w-4 h-4 text-mid-purple" />
              <span className="text-mid-purple text-sm font-mono uppercase tracking-wider">
                Our Approach
              </span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-display font-bold text-white mb-6">
              What Makes Us Different
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {aboutInfo.approach.map((item, index) => (
              <div
                key={index}
                className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-light-pink/50 transition-all duration-300 hover:-translate-y-2 group"
                onMouseEnter={() => playSoundIfEnabled('hover')}
              >
                <div className="w-12 h-12 bg-gradient-to-br from-light-pink to-mid-purple rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <p className="text-white text-lg leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-light-pink/20 to-light-pink/5 rounded-3xl p-8 border border-light-pink/20">
              <Heart className="w-12 h-12 text-light-pink mb-4" />
              <h3 className="text-2xl font-bold text-white mb-4">Passion</h3>
              <p className="text-mid-grey leading-relaxed">
                We're passionate about theatre and children's development. Every workshop is designed 
                with love and care to nurture young talent and creativity.
              </p>
            </div>

            <div className="bg-gradient-to-br from-mid-purple/20 to-mid-purple/5 rounded-3xl p-8 border border-mid-purple/20">
              <Users className="w-12 h-12 text-mid-purple mb-4" />
              <h3 className="text-2xl font-bold text-white mb-4">Community</h3>
              <p className="text-mid-grey leading-relaxed">
                We build a supportive community where every child feels valued, heard, and encouraged 
                to express themselves freely and confidently.
              </p>
            </div>

            <div className="bg-gradient-to-br from-light-yellow/20 to-light-yellow/5 rounded-3xl p-8 border border-light-yellow/20">
              <Award className="w-12 h-12 text-light-yellow mb-4" />
              <h3 className="text-2xl font-bold text-white mb-4">Excellence</h3>
              <p className="text-mid-grey leading-relaxed">
                Professional training methods adapted for young performers. We maintain high standards 
                while keeping workshops fun and engaging.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 lg:py-24 bg-gradient-to-b from-black to-black">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-light-yellow/10 rounded-full px-4 py-2 mb-4">
              <Sparkles className="w-4 h-4 text-light-yellow" />
              <span className="text-light-yellow text-sm font-mono uppercase tracking-wider">
                FAQs
              </span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-display font-bold text-white mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq) => (
              <AccordionItem 
                key={faq.id} 
                value={`item-${faq.id}`}
                className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 px-6 overflow-hidden"
              >
                <AccordionTrigger 
                  className="text-white hover:text-light-pink transition-colors py-6 text-left font-semibold"
                  onClick={() => playSoundIfEnabled('click')}
                >
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-mid-grey pb-6 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="bg-gradient-to-r from-light-pink/20 via-mid-purple/20 to-light-yellow/20 rounded-3xl p-12 lg:p-16 text-center border border-light-pink/20">
            <Sparkles className="w-16 h-16 text-light-pink mx-auto mb-6 animate-pulse" />
            <h2 className="text-4xl lg:text-5xl font-display font-bold text-white mb-6">
              Ready to Join Our Creative Community?
            </h2>
            <p className="text-xl text-mid-grey mb-8 max-w-2xl mx-auto">
              Give your child the gift of confidence, creativity, and unforgettable experiences
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/workshops"
                className="cta-button large bg-light-pink text-black hover:bg-mid-yellow transition-all duration-300 inline-flex items-center gap-2"
                onClick={() => playSoundIfEnabled('success')}
              >
                View Workshops
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/contact"
                className="cta-button large bg-white/10 text-white hover:bg-white/20 transition-all duration-300"
                onClick={() => playSoundIfEnabled('click')}
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
